import React from 'react';
import { Building2, Globe, HelpCircle, Users, Settings2, LogOut, X, Menu } from 'lucide-react';
import { User } from '../types';

type View = 'new' | 'list' | 'dashboard';

interface AppHeaderProps {
  isAuthenticated: boolean;
  currentUser: User | null;
  isAdmin: boolean;
  currentView: View;
  setCurrentView: (view: View) => void;
  iniciarNovoPlano: () => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (valor: boolean | ((v: boolean) => boolean)) => void;
  emendaNotifCount: number;
  selectedCnes: string;
  parseCnesList: (cnes: string | undefined) => string[];
  onTrocarCnes: (cnes: string) => void;
  cnesCadastroCache: Record<string, string>;
  abrirEdicaoSiteTransparencia: () => void;
  faqDisponivel: boolean;
  abrirFaq: () => void;
  abrirDisponibilizarEmenda: () => void;
  abrirGestaoUsuarios: () => void;
  abrirCadastroCnes: () => void;
  abrirConfiguracoes: () => void;
  handleLogout: () => void;
}

// Cabeçalho fixo: identidade, navegação principal, CNES ativo e ações do usuário
export const AppHeader: React.FC<AppHeaderProps> = ({
  isAuthenticated, currentUser, isAdmin, currentView, setCurrentView, iniciarNovoPlano,
  mobileMenuOpen, setMobileMenuOpen, emendaNotifCount, selectedCnes, parseCnesList, onTrocarCnes,
  cnesCadastroCache, abrirEdicaoSiteTransparencia, faqDisponivel, abrirFaq,
  abrirDisponibilizarEmenda, abrirGestaoUsuarios, abrirCadastroCnes, abrirConfiguracoes, handleLogout
}) => (
    <header className="bg-[#08162D] sticky top-0 z-50 border-b-2 border-[#D92B2B]" style={{ fontFamily: "Inter, 'Segoe UI', sans-serif" }}>
      {/* Barra principal — altura fixa 72px */}
      <div className="h-[72px] flex items-center px-4 lg:px-8">
        <div className="w-full flex items-center justify-between gap-4">

          {/* ── Bloco Esquerdo: Logo + Identidade ── */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <img
              src="/img/brasao-3-texto-branco.png"
              alt="Governo do Estado de São Paulo – Secretaria de Estado da Saúde"
              className="h-14 lg:h-16 w-auto"
            />
            <div className="hidden sm:block border-l border-white/20 pl-3">
              <p className="text-[15px] font-black text-white whitespace-nowrap leading-tight tracking-tight">Plano de Trabalho</p>
              <p className="text-[11px] text-[#B8C1D1] whitespace-nowrap leading-tight">SES · Secretaria de Estado da Saúde</p>
            </div>
          </div>

          {/* ── Bloco Centro: Nav Desktop ── */}
          {isAuthenticated && (
            <nav aria-label="Navegação principal" className="hidden lg:flex items-center gap-1 flex-1 justify-center">
              {currentUser?.role !== 'user' && (
                <button
                  onClick={iniciarNovoPlano}
                  aria-current={currentView === 'new' ? 'page' : undefined}
                  className={`relative px-4 py-2 rounded-lg text-[13px] font-semibold tracking-wide transition-all duration-150 ${
                    currentView === 'new'
                      ? 'text-white bg-white/10'
                      : 'text-[#B8C1D1] hover:text-white hover:bg-white/8'
                  }`}
                >
                  {currentView === 'new' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-[#D92B2B] rounded-full" />}
                  Novo Plano
                </button>
              )}
              {currentUser?.role === 'admin' && (
                <button
                  onClick={() => { abrirDisponibilizarEmenda(); setMobileMenuOpen(false); }}
                  className="relative px-4 py-2 rounded-lg text-[13px] font-semibold tracking-wide text-[#B8C1D1] hover:text-white hover:bg-white/8 transition-all duration-150"
                  title="Disponibilizar Emenda para Beneficiário"
                >
                  Disponibilizar Emenda
                  {emendaNotifCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-green-500 text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 shadow">
                      {emendaNotifCount}
                    </span>
                  )}
                </button>
              )}
              <button
                onClick={() => { setCurrentView('list'); setMobileMenuOpen(false); }}
                aria-current={currentView === 'list' ? 'page' : undefined}
                className={`relative px-4 py-2 rounded-lg text-[13px] font-semibold tracking-wide transition-all duration-150 ${
                  currentView === 'list'
                    ? 'text-white bg-white/10'
                    : 'text-[#B8C1D1] hover:text-white hover:bg-white/8'
                }`}
              >
                {currentView === 'list' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-[#D92B2B] rounded-full" />}
                Meus Planos
              </button>
              {(isAdmin || currentUser?.role === 'intermediate') && (
                <button
                  onClick={() => { setCurrentView('dashboard'); setMobileMenuOpen(false); }}
                  aria-current={currentView === 'dashboard' ? 'page' : undefined}
                  className={`relative px-4 py-2 rounded-lg text-[13px] font-semibold tracking-wide transition-all duration-150 ${
                    currentView === 'dashboard'
                      ? 'text-white bg-white/10'
                      : 'text-[#B8C1D1] hover:text-white hover:bg-white/8'
                  }`}
                >
                  {currentView === 'dashboard' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-[#D92B2B] rounded-full" />}
                  Dashboard
                </button>
              )}
            </nav>
          )}

          {/* ── Bloco Direito: User + Ações ── */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isAuthenticated && (
              <>
                {/* Seletor CNES múltiplo */}
                {currentUser?.role === 'user' && (currentUser?.cnes || '').includes(',') && (
                  <div className="hidden md:flex items-center gap-2 mr-2">
                    <Building2 className="w-3.5 h-3.5 text-[#B8C1D1]" />
                    <select
                      value={selectedCnes || parseCnesList(currentUser?.cnes)[0]}
                      onChange={(e) => onTrocarCnes(e.target.value)}
                      className="bg-white/10 text-white text-xs rounded-lg border border-white/20 px-2 py-1.5 focus:ring-2 focus:ring-[#D92B2B] focus:border-[#D92B2B]"
                      title="Selecione o CNES ativo"
                    >
                      {parseCnesList(currentUser?.cnes).map(cnes => (
                        <option key={cnes} value={cnes}>CNES: {cnes}</option>
                      ))}
                    </select>
                  </div>
                )}
                {currentUser?.role === 'user' && !(currentUser?.cnes || '').includes(',') && currentUser?.cnes && (
                  <span className="text-[11px] text-[#B8C1D1] hidden md:block mr-1">CNES {currentUser.cnes}</span>
                )}
                {currentUser?.role === 'user' && selectedCnes && (
                  <button
                    onClick={abrirEdicaoSiteTransparencia}
                    className="w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10 transition-all duration-150"
                    title={cnesCadastroCache[selectedCnes] ? `Site de transparência: ${cnesCadastroCache[selectedCnes]}` : 'Cadastrar site de transparência'}
                    aria-label="Site de transparência do CNES"
                  >
                    <Globe className="w-4 h-4" />
                  </button>
                )}

                {/* Central de Dúvidas */}
                {faqDisponivel && (
                  <button
                    onClick={abrirFaq}
                    className="w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10 transition-all duration-150"
                    title="Central de Dúvidas"
                    aria-label="Abrir central de dúvidas"
                  >
                    <HelpCircle style={{ width: 18, height: 18 }} />
                  </button>
                )}

                {/* Separador */}
                <div className="hidden sm:block w-px h-8 bg-white/15 mx-2" />

                {/* Avatar + Nome */}
                <div className="hidden sm:flex items-center gap-2.5 mr-1">
                  <div className="w-8 h-8 rounded-full bg-[#D92B2B] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-black leading-none">
                      {(currentUser?.name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="leading-tight">
                    <p className="text-[13px] font-semibold text-white leading-none">{currentUser?.name}</p>
                    <p className="text-[10px] text-[#B8C1D1] uppercase tracking-wider leading-none mt-0.5">
                      {currentUser?.role === 'admin' ? 'Administrador' : currentUser?.role === 'intermediate' ? 'Intermediário' : 'Usuário'}
                    </p>
                  </div>
                </div>

                {/* Separador */}
                <div className="hidden sm:block w-px h-8 bg-white/15 mx-1" />

                {/* Ação: Gerenciar Usuários */}
                {currentUser?.role === 'admin' && (
                  <button
                    onClick={() => abrirGestaoUsuarios()}
                    className="w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10 transition-all duration-150"
                    title="Gerenciar Usuários"
                    aria-label="Gerenciar Usuários"
                  >
                    <Users className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                  </button>
                )}

                {/* Ação: Cadastro dos CNES (sites de transparência) */}
                {currentUser?.role === 'admin' && (
                  <button
                    onClick={() => abrirCadastroCnes()}
                    className="w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10 transition-all duration-150"
                    title="Cadastro dos CNES (sites de transparência)"
                    aria-label="Cadastro dos CNES"
                  >
                    <Globe className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                  </button>
                )}

                {/* Ação: Configurações */}
                {currentUser?.role === 'admin' && (
                  <button
                    onClick={() => abrirConfiguracoes()}
                    className="w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10 transition-all duration-150"
                    title="Configurar Listas (Programas, Metas, Naturezas)"
                    aria-label="Configurações"
                  >
                    <Settings2 className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                  </button>
                )}

                {/* Ação: Sair */}
                <button
                  onClick={handleLogout}
                  className="w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-[#D92B2B] hover:bg-white/10 transition-all duration-150"
                  title="Sair"
                  aria-label="Sair da conta"
                >
                  <LogOut className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
                </button>

                {/* Hambúrguer (mobile/tablet) */}
                <button
                  onClick={() => setMobileMenuOpen(v => !v)}
                  className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-[#B8C1D1] hover:text-white hover:bg-white/10 transition-all duration-150 ml-1"
                  aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                  aria-expanded={mobileMenuOpen}
                  aria-controls="menu-mobile"
                >
                  {mobileMenuOpen ? <X style={{ width: 20, height: 20 }} /> : <Menu style={{ width: 20, height: 20 }} />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Menu mobile/tablet — slide down ── */}
      {isAuthenticated && mobileMenuOpen && (
        <nav id="menu-mobile" aria-label="Menu principal" className="lg:hidden bg-[#0c1e3e] border-t border-white/10 px-4 pb-4 pt-3 flex flex-col gap-1">
          {currentUser?.role !== 'user' && (
            <button
              onClick={iniciarNovoPlano}
              aria-current={currentView === 'new' ? 'page' : undefined}
              className={`text-left px-4 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${currentView === 'new' ? 'bg-white/10 text-white' : 'text-[#B8C1D1] hover:text-white hover:bg-white/8'}`}
            >Novo Plano</button>
          )}
          {currentUser?.role === 'admin' && (
            <button
              onClick={() => { abrirDisponibilizarEmenda(); setMobileMenuOpen(false); }}
              className="text-left px-4 py-2.5 rounded-lg text-[13px] font-semibold text-[#B8C1D1] hover:text-white hover:bg-white/8 transition-colors flex items-center gap-2"
            >
              Disponibilizar Emenda
              {emendaNotifCount > 0 && (
                <span className="bg-green-500 text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{emendaNotifCount}</span>
              )}
            </button>
          )}
          <button
            onClick={() => { setCurrentView('list'); setMobileMenuOpen(false); }}
            aria-current={currentView === 'list' ? 'page' : undefined}
            className={`text-left px-4 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${currentView === 'list' ? 'bg-white/10 text-white' : 'text-[#B8C1D1] hover:text-white hover:bg-white/8'}`}
          >Meus Planos</button>
          {(isAdmin || currentUser?.role === 'intermediate') && (
            <button
              onClick={() => { setCurrentView('dashboard'); setMobileMenuOpen(false); }}
              aria-current={currentView === 'dashboard' ? 'page' : undefined}
              className={`text-left px-4 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${currentView === 'dashboard' ? 'bg-white/10 text-white' : 'text-[#B8C1D1] hover:text-white hover:bg-white/8'}`}
            >Dashboard</button>
          )}
          {currentUser?.role === 'admin' && (
            <button
              onClick={() => { abrirCadastroCnes(); setMobileMenuOpen(false); }}
              className="text-left px-4 py-2.5 rounded-lg text-[13px] font-semibold text-[#B8C1D1] hover:text-white hover:bg-white/8 transition-colors"
            >Cadastro dos CNES</button>
          )}
          {faqDisponivel && (
            <button
              onClick={abrirFaq}
              className="text-left px-4 py-2.5 rounded-lg text-[13px] font-semibold text-[#B8C1D1] hover:text-white hover:bg-white/8 transition-colors"
            >Central de Dúvidas</button>
          )}
          {/* Info usuário mobile */}
          <div className="mt-2 pt-3 border-t border-white/10 flex items-center gap-3 px-1">
            <div className="w-8 h-8 rounded-full bg-[#D92B2B] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-black">{(currentUser?.name || 'U').charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <p className="text-[13px] font-semibold text-white">{currentUser?.name}</p>
              <p className="text-[10px] text-[#B8C1D1] uppercase tracking-wider">
                {currentUser?.role === 'admin' ? 'Administrador' : currentUser?.role === 'intermediate' ? 'Intermediário' : 'Usuário'}
              </p>
            </div>
          </div>
        </nav>
      )}
    </header>
);
